import { Injectable, NgZone } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { EventService } from './events.service';
import {
  HEARTBEAT_MS,
  MAX_ERROR_EVENTS,
  MAX_EVENTS_PER_SESSION,
  PersistedSession,
  STORAGE_KEY,
  TELEMETRY_EVENT,
  Tap,
  Transition,
  describeTarget,
  ignoredError,
  parsePriorSession,
  pushTransition,
  stallGap,
  telemetryEnabled,
  trimStack,
  truncate,
} from './client-telemetry-core';

declare var Penmate;

// Diagnostic probe for the host, default OFF: `Penmate.env.clientTelemetryEnabled`
// "true" / "1" arms it. It keeps a ring of the last navigations (with the stack
// that pushed them), the last tap, heartbeat stalls and the first errors, and
// posts them to /metrics as the "client-telemetry" event. The ring is written
// to localStorage on every change and every beat, so a session that wedged
// and had to be killed reports itself as "prior_session" on the next boot.
// Everything runs outside the Angular zone and never throws into the app.
@Injectable({ providedIn: 'root' })
export class ClientTelemetryService {
  private started = false;
  private sent = 0;
  private errorsSent = 0;
  private startedAt = 0;
  private lastHeartbeatAt = 0;
  private hiddenDuringBeat = false;
  private transitions: Transition[] = [];
  private lastTap: Tap | null = null;

  constructor(
    private ngZone: NgZone,
    private router: Router,
    private eventService: EventService,
  ) {}

  start(): void {
    if (this.started) return;
    this.started = true;
    let enabled = false;
    try {
      const env = typeof Penmate !== 'undefined' && Penmate ? Penmate.env : null;
      enabled = telemetryEnabled(env ? env.clientTelemetryEnabled : undefined);
    } catch (e) {
      enabled = false;
    }
    if (!enabled) return;
    try {
      this.ngZone.runOutsideAngular(() => this.arm());
    } catch (e) {
      // diagnostics never block boot
    }
  }

  private arm(): void {
    const now = Date.now();
    this.startedAt = now;
    this.lastHeartbeatAt = now;

    const prior = this.takePriorSession(now);
    this.persist(false);
    this.send('boot', {});
    if (prior && !prior.ended) {
      this.send('prior_session', {
        path: prior.path,
        transitions: prior.transitions,
        last_tap: prior.last_tap,
        age_ms: prior.age_ms,
        prior_session_ms: prior.last_hb_at - prior.started_at,
      });
    }

    this.wrapHistory('pushState');
    this.wrapHistory('replaceState');
    window.addEventListener('popstate', () => {
      this.record({ url: this.path(), at: Date.now(), via: 'popstate' });
    });

    this.router.events.subscribe(event => {
      try {
        if (event instanceof NavigationStart) {
          this.record({ url: truncate(event.url, 200), at: Date.now(), via: 'nav:start', stack: trimStack(new Error().stack, 10) });
        } else if (event instanceof NavigationEnd) {
          this.record({ url: truncate(event.urlAfterRedirects, 200), at: Date.now(), via: 'nav:end' });
        } else if (event instanceof NavigationCancel) {
          this.record({ url: truncate(event.url, 200), at: Date.now(), via: 'nav:cancel', note: truncate(event.reason, 120) });
        } else if (event instanceof NavigationError) {
          const err: any = event.error;
          this.record({ url: truncate(event.url, 200), at: Date.now(), via: 'nav:error', note: truncate(err && err.message ? err.message : err, 120) });
        }
      } catch (e) {
        // ignore
      }
    });

    const tap = (via: string) => (e: Event) => {
      try {
        this.lastTap = { at: Date.now(), target: describeTarget(e.target as any), path: this.path(), via };
        this.persist(false);
      } catch (e2) {
        // ignore
      }
    };
    document.addEventListener('touchstart', tap('touchstart'), { capture: true, passive: true } as any);
    document.addEventListener('click', tap('click'), true);

    window.addEventListener('error', (e: any) => this.onError('error', e && (e.error || e.message)));
    window.addEventListener('unhandledrejection', (e: any) => this.onError('rejection', e && e.reason));

    document.addEventListener('visibilitychange', () => {
      try {
        if (document.visibilityState === 'hidden') {
          this.hiddenDuringBeat = true;
          this.persist(false);
        }
      } catch (e) {
        // ignore
      }
    });
    window.addEventListener('pagehide', () => this.persist(true));

    setInterval(() => {
      try {
        const now = Date.now();
        const gap = stallGap(this.lastHeartbeatAt, now);
        const hidden = this.hiddenDuringBeat || document.visibilityState === 'hidden';
        this.hiddenDuringBeat = false;
        this.lastHeartbeatAt = now;
        this.persist(false);
        if (gap !== null) this.send('stall', { gap_ms: gap, hidden_during_gap: hidden });
      } catch (e) {
        // ignore
      }
    }, HEARTBEAT_MS);
  }

  private wrapHistory(name: 'pushState' | 'replaceState'): void {
    try {
      const history: any = window.history;
      const original = history[name];
      if (typeof original !== 'function') return;
      const self = this;
      history[name] = function() {
        try {
          const url = arguments[2];
          self.record({
            url: url === undefined || url === null ? self.path() : truncate(url, 200),
            at: Date.now(),
            via: name,
            stack: trimStack(new Error().stack),
          });
        } catch (e) {
          // ignore
        }
        return original.apply(this, arguments);
      };
    } catch (e) {
      // ignore
    }
  }

  private record(entry: Transition): void {
    this.transitions = pushTransition(this.transitions, entry);
    this.persist(false);
  }

  private onError(errorKind: string, reason: any): void {
    try {
      const message = truncate(reason && reason.message ? reason.message : reason, 300);
      if (ignoredError(message)) return;
      if (this.errorsSent >= MAX_ERROR_EVENTS) return;
      this.errorsSent += 1;
      this.send('error', { error_kind: errorKind, message, stack: trimStack(reason && reason.stack) });
    } catch (e) {
      // ignore
    }
  }

  private send(kind: string, extra: { [key: string]: any }): void {
    if (this.sent >= MAX_EVENTS_PER_SESSION) return;
    this.sent += 1;
    try {
      const payload = Object.assign(
        {
          kind,
          path: this.path(),
          transitions: this.transitions.slice(),
          last_tap: this.lastTap,
          session_ms: Date.now() - this.startedAt,
          visible: typeof document.visibilityState === 'string' ? document.visibilityState : null,
          ua: truncate(navigator.userAgent, 300),
        },
        extra || {},
      );
      const result: any = this.eventService.track(TELEMETRY_EVENT, payload);
      if (result && typeof result.catch === 'function') result.catch(() => {});
    } catch (e) {
      // ignore
    }
  }

  private takePriorSession(now: number): (PersistedSession & { age_ms: number }) | null {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      window.localStorage.removeItem(STORAGE_KEY);
      return parsePriorSession(raw, now);
    } catch (e) {
      return null;
    }
  }

  private persist(ended: boolean): void {
    try {
      const record: PersistedSession = {
        path: this.path(),
        started_at: this.startedAt,
        last_hb_at: this.lastHeartbeatAt,
        ended,
        transitions: this.transitions,
        last_tap: this.lastTap,
      };
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    } catch (e) {
      // ignore
    }
  }

  private path(): string {
    try {
      return truncate(window.location.pathname + window.location.search, 200);
    } catch (e) {
      return '';
    }
  }
}
